import { useIntl } from 'react-intl';

import { STATIC_COLUMNS } from '~/common/constants/search';
import { EntityVisibilityType } from '~/components/Shared/EntityVisibilitySelect/types';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Filter } from '~/evifields/src/types/Filter';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';

import { SavedSearch } from '../search/types/search';
import { buildResultTable } from './AdvancedSearch.utils';
import { SEARCH_ENTITY } from './constants';
import { SearchQuery } from './types';
import { testIsFilterEmpty } from './utils/build-query';

interface UseSaveSearchParams {
  savedSearch: SavedSearch | undefined;
  query: SearchQuery;
  selectedFilters: Filter[];
  columnOrder: string[];
}

export const useSaveSearch = ({
  savedSearch,
  query,
  selectedFilters,
  columnOrder,
}: UseSaveSearchParams) => {
  const { navigate } = useRouting();
  const { toast } = useToast();
  const intl = useIntl();
  const [postSavedSearch] = api.endpoints.postSavedSearch.useMutation();
  const [putSavedSearch] = api.endpoints.putSavedSearch.useMutation();

  const buildPayload = () => ({
    query,
    filters: selectedFilters.filter(testIsFilterEmpty).map((f) => f.fieldId),
    resultTable: buildResultTable(columnOrder, STATIC_COLUMNS[SEARCH_ENTITY]),
  });

  const handleSaveAs = async (
    data: { name: string } & EntityVisibilityType,
  ) => {
    try {
      const response = await postSavedSearch({
        name: data.name,
        visibility: data.visibility,
        userIds: data.userIds,
        departmentIds: data.departmentIds,
        ...buildPayload(),
      }).unwrap();
      if (response) {
        toast({
          status: 'success',
          message: intl.formatMessage(
            {
              id: 'AdvancedSearch.searchSavedMessage',
              defaultMessage: 'Search {searchName} has been saved.',
              description:
                'Success message when a search is saved, searchName: name of the search (any name). Example: searchName = "Contract Review Dashboard"',
            },
            { searchName: response.name },
          ),
        });
        navigate(`/search/${response.id}`);
      }
    } catch (error: any) {
      toast({ status: 'danger', message: error.message });
    }
  };

  const handleSave = async () => {
    if (!savedSearch) return;
    try {
      const response = await putSavedSearch({
        id: savedSearch.id,
        savedSearch: {
          name: savedSearch.name,
          visibility: savedSearch.visibility,
          userIds: savedSearch.userIds,
          departmentIds: savedSearch.departmentIds,
          ...buildPayload(),
        },
      }).unwrap();
      if (response) {
        toast({
          status: 'success',
          message: intl.formatMessage(
            {
              id: 'AdvancedSearch.searchUpdatedMessage',
              defaultMessage: 'Search {searchName} has been saved.',
              description:
                'Success message when a search is updated, searchName: name of the search (any name). Example: searchName = "Contract Review Dashboard"',
            },
            { searchName: response.name },
          ),
        });
      }
    } catch (error: any) {
      toast({ status: 'danger', message: error.message });
    }
  };

  return { handleSave, handleSaveAs };
};
